// dRPC's public Polygon endpoint sometimes returns its "over 10000 blocks"
// error for much smaller requests (including 2,000-block ranges). Keep the
// first request conservative; adaptive splitting below handles stricter nodes.
const MAX_LOG_RANGE = 500n;
const MAX_LOOKBACK = 2_000_000n;
const CREATION_BLOCK_BUFFER = 20_000n;

// Each batch assigns one range to each RPC client concurrently, then pauses
// before the next batch to avoid sustained bursts against anonymous tiers.
export const LOG_QUERY_BATCH_DELAY_MS = 300;

export const waitForNextLogBatch = () => new Promise<void>((resolve) => {
  globalThis.setTimeout(resolve, LOG_QUERY_BATCH_DELAY_MS);
});

const isLogRangeLimitError = (error: unknown) => {
  let current = error;
  for (let depth = 0; depth < 20 && current && typeof current === "object"; depth += 1) {
    const candidate = current as { message?: string; details?: string; cause?: unknown };
    const text = `${candidate.message || ""} ${candidate.details || ""}`.toLowerCase();
    if (
      (text.includes("range") || text.includes("response size"))
      && (text.includes("block") || text.includes("limit") || text.includes("large"))
    ) return true;
    current = candidate.cause;
  }
  return false;
};

export const getLogsWithAdaptiveRange = async <T>(
  fromBlock: bigint,
  toBlock: bigint,
  fetchLogs: (fromBlock: bigint, toBlock: bigint) => Promise<readonly T[]>,
): Promise<T[]> => {
  try {
    return [...await fetchLogs(fromBlock, toBlock)];
  } catch (error) {
    if (!isLogRangeLimitError(error) || fromBlock >= toBlock) throw error;
    const midpoint = fromBlock + (toBlock - fromBlock) / 2n;
    // Split sequentially. Parallel recursion can create dozens of requests at
    // once and causes the public dRPC endpoint to reject otherwise valid calls.
    const older = await getLogsWithAdaptiveRange(fromBlock, midpoint, fetchLogs);
    const newer = await getLogsWithAdaptiveRange(midpoint + 1n, toBlock, fetchLogs);
    return [...older, ...newer];
  }
};

export const getHistoryStartBlock = (latestBlock: bigint, createdAt?: string) => {
  const createdAtMs = createdAt ? Date.parse(createdAt) : Number.NaN;
  const ageSeconds = Number.isFinite(createdAtMs)
    ? Math.max(0, Math.ceil((Date.now() - createdAtMs) / 1_000))
    : null;
  const estimatedLookback = ageSeconds === null
    ? MAX_LOOKBACK
    : BigInt(ageSeconds) + CREATION_BLOCK_BUFFER;
  const lookback = estimatedLookback < MAX_LOOKBACK ? estimatedLookback : MAX_LOOKBACK;

  return latestBlock > lookback ? latestBlock - lookback : 0n;
};

export const getDescendingBlockRanges = (latestBlock: bigint, earliestBlock: bigint) => {
  const ranges: Array<{ fromBlock: bigint; toBlock: bigint }> = [];
  let endBlock = latestBlock;

  while (endBlock >= earliestBlock) {
    const startBlock = endBlock - earliestBlock >= MAX_LOG_RANGE
      ? endBlock - MAX_LOG_RANGE + 1n
      : earliestBlock;
    ranges.push({ fromBlock: startBlock, toBlock: endBlock });
    if (startBlock === earliestBlock) break;
    endBlock = startBlock - 1n;
  }

  return ranges;
};
